import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaBuilding, FaClock, FaMoneyBillWave, FaCreditCard, FaCcVisa, FaCcMastercard, FaCheckCircle, FaTimesCircle, FaArrowLeft, FaChevronDown, FaChevronUp, FaSwimmingPool, FaExclamationCircle } from 'react-icons/fa';
import { getClubPaymentInfo } from '../../../api/sportsAppointmentService';
import { initiateGeneralPayment } from '../../../api/paymentService';

const formatVatIncluded = (amount) =>
    `${Number(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL (KDV dahil)`;

const ClubPayment = () => {
    const [taxNumber, setTaxNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);
    const [paymentMode, setPaymentMode] = useState('full'); // 'full' | 'monthly' | 'daily'
    const [expandedDays, setExpandedDays] = useState({}); // { dateKey: true/false }
    const [selectedLaneCount, setSelectedLaneCount] = useState(1); // For pool lane selection
    const [facilityTab, setFacilityTab] = useState({}); // { facilityName: 'unpaid' | 'paid' }
    const [selectedPoolSessions, setSelectedPoolSessions] = useState({}); // { "assignmentId_dateKey": laneCount }
    const [activeFacilityKey, setActiveFacilityKey] = useState(null); // Currently selected facility tab

    // Payment states
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentData, setPaymentData] = useState({
        cardName: '',
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: ''
    });
    const [paymentErrors, setPaymentErrors] = useState({});
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentHtml, setPaymentHtml] = useState(null);
    const [paymentOutcome, setPaymentOutcome] = useState(null);
    const [resultMessage, setResultMessage] = useState('');
    const [paidAmount, setPaidAmount] = useState(0);
    const iframeRef = useRef(null);

    // Payment result listener
    useEffect(() => {
        const handleMessage = (event) => {
            const data = event.data;
            if (!data || data.type !== 'PAYMENT_RESULT') return;
            if (data.status === 'success') {
                setPaymentOutcome('success');
                setResultMessage('Kulüp ödemesi başarıyla tamamlandı.');
                setPaymentHtml(null);
                // Ödeme başarılı → veriyi yenile
                if (taxNumber) {
                    getClubPaymentInfo(taxNumber.trim()).then(result => {
                        if (result?.isSuccess && result?.data) setPaymentInfo(result.data);
                    }).catch(() => { });
                }
            } else if (data.status === 'failed') {
                setPaymentOutcome('failed');
                setResultMessage(data.error ? decodeURIComponent(data.error) : 'Ödeme işlemi başarısız oldu.');
                setPaymentHtml(null);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [taxNumber]);

    const handleIframeLoad = () => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        try {
            const href = iframe.contentWindow?.location?.href;
            if (!href) return;
            const iframeUrl = new URL(href);
            const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
            const err = iframeUrl.searchParams.get('error');
            if (paymentStatus === 'success') {
                setPaymentOutcome('success');
                setResultMessage('Kulüp ödemesi başarıyla tamamlandı.');
                setPaymentHtml(null);
                // Ödeme başarılı → veriyi yenile
                if (taxNumber) {
                    getClubPaymentInfo(taxNumber.trim()).then(result => {
                        if (result?.isSuccess && result?.data) setPaymentInfo(result.data);
                    }).catch(() => { });
                }
            } else if (paymentStatus === 'failed') {
                setPaymentOutcome('failed');
                setResultMessage(err ? decodeURIComponent(err) : 'Ödeme işlemi başarısız oldu.');
                setPaymentHtml(null);
            }
        } catch { /* cross-origin */ }
    };

    const detectCardType = (number) => {
        const cleaned = number.replace(/\s/g, '');
        if (cleaned.startsWith('4')) return '1';
        if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return '2';
        return null;
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!taxNumber.trim()) return;
        setLoading(true);
        setError('');
        setPaymentInfo(null);
        setActiveFacilityKey(null);
        setSearched(true);
        setShowPaymentForm(false);
        setPaymentOutcome(null);
        setPaymentHtml(null);
        setSelectedPoolSessions({});
        setFacilityTab({});
        try {
            const result = await getClubPaymentInfo(taxNumber.trim());
            if (result?.isSuccess && result?.data) {
                setPaymentInfo(result.data);
                setActiveFacilityKey(result.data.assignments?.[0]?.facilityName || null);
                // paymentMode varsayılan olarak 'full' kalacak
            } else {
                setError(result?.resultMessage || 'Kulüp bilgileri bulunamadı.');
            }
        } catch (err) {
            console.error(err);
            setError('Sorgulama sırasında hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Active tab'ın facilityName'i assignments'taki facilityName ile eşleşir.
    // Ancak regionBreakdowns farklı facilityId kullanır (parent vs child).
    // Bu yüzden her şeyi clubApplicationId üzerinden eşleştiriyoruz.
    const getSelectedClubApplicationId = () => {
        if (!activeFacilityKey) return paymentInfo?.assignments?.[0]?.clubApplicationId;
        const assignment = (paymentInfo?.assignments || []).find(
            (a) => (a.facilityName || 'Bilinmeyen Tesis') === activeFacilityKey
        );
        return assignment?.clubApplicationId || paymentInfo?.assignments?.[0]?.clubApplicationId;
    };

    const getCurrentFacilityName = () => {
        const assignmentFacilityNames = [...new Set((paymentInfo?.assignments || []).map(
            (assignment) => assignment.facilityName || 'Bilinmeyen Tesis'
        ))];
        if (activeFacilityKey && assignmentFacilityNames.includes(activeFacilityKey)) {
            return activeFacilityKey;
        }
        return assignmentFacilityNames[0] || paymentInfo?.regionBreakdowns?.[0]?.facilityName || null;
    };

    // Seçili tesisin slotlarını clubApplicationId ile eşleştir
    const getSelectedAssignments = () => {
        const clubAppId = getSelectedClubApplicationId();
        if (!clubAppId) return [];
        return (paymentInfo?.assignments || []).filter(
            (a) => a.clubApplicationId === clubAppId
        );
    };

    // Ödeme payload'ı için assignments'taki facilityId'yi kullan (child ID)
    const getSelectedFacilityId = () => {
        const assignments = getSelectedAssignments();
        return assignments[0]?.facilityId || paymentInfo?.regionBreakdowns?.[0]?.facilityId || null;
    };

    // regionBreakdowns'u clubApplicationId ile eşleştir
    const getSelectedBreakdown = () => {
        const clubAppId = getSelectedClubApplicationId();
        if (!clubAppId) return paymentInfo?.regionBreakdowns?.[0] || null;
        return paymentInfo?.regionBreakdowns?.find(
            (r) => r.clubApplicationId === clubAppId
        ) || paymentInfo?.regionBreakdowns?.[0] || null;
    };

    const getSelectedDebts = () => {
        const facilityId = getSelectedFacilityId();
        if (!facilityId) return paymentInfo?.additionalDebts || [];
        return (paymentInfo?.additionalDebts || []).filter(
            (debt) => Number(debt.facilityId) === Number(facilityId)
        );
    };

    const getSelectedClubApplicationIds = () => {
        const ids = getSelectedAssignments()
            .map((assignment) => Number(assignment.clubApplicationId))
            .filter(Boolean);
        const selectedBreakdown = getSelectedBreakdown();
        if (ids.length === 0 && selectedBreakdown?.clubApplicationId) {
            ids.push(Number(selectedBreakdown.clubApplicationId));
        }
        return [...new Set(ids)];
    };

    // Detect if any assignment is for a pool facility
    const isPoolFacility = () => {
        const assignments = getSelectedAssignments();
        if (!assignments.length) return false;
        return assignments.some(a =>
            (a.facilityTypeName || '').toLowerCase().includes('havuz')
        );
    };

    // Get lane price from assignments
    const getLanePricePerHour = () => {
        const assignments = getSelectedAssignments();
        if (!assignments.length) return 0;
        const poolAssignment = assignments.find(a =>
            (a.facilityTypeName || '').toLowerCase().includes('havuz') && a.lanePricePerHour > 0
        );
        return poolAssignment?.lanePricePerHour || 0;
    };

    // Calculate lane count from person count
    const getLaneCountFromPersons = (personCount) => {
        if (personCount <= 13) return 1;
        if (personCount <= 25) return 2;
        if (personCount <= 37) return 3;
        if (personCount <= 49) return 4;
        return 5;
    };

    const getDayName = (dayNum) => {
        const days = { 0: 'Pazar', 1: 'Pazartesi', 2: 'Salı', 3: 'Çarşamba', 4: 'Perşembe', 5: 'Cuma', 6: 'Cumartesi' };
        return days[dayNum] || dayNum;
    };

    const normalizeDate = (value) => {
        const date = value instanceof Date ? new Date(value) : new Date(value);
        date.setHours(0, 0, 0, 0);
        return date;
    };

    const getDateKey = (value) => {
        const date = normalizeDate(value);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getSlotPeriod = (slot) => {
        const region = paymentInfo?.regionBreakdowns?.find(r => r.clubApplicationId === slot.clubApplicationId);
        const fallbackStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        const fallbackEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
        const membershipStart = region?.membershipStartDate ? normalizeDate(region.membershipStartDate) : fallbackStart;
        const membershipEnd = region?.membershipEndDate ? normalizeDate(region.membershipEndDate) : fallbackEnd;
        const assignmentStart = slot?.createdAt ? normalizeDate(slot.createdAt) : membershipStart;

        return {
            start: assignmentStart > membershipStart ? assignmentStart : membershipStart,
            end: membershipEnd
        };
    };

    /**
     * Calculates the effective price for a single assignment slot,
     * splitting hours between day and night by the configured boundary.
     */
    const getEffectiveSlotPrice = (slot) => {
        if (!slot) return 0;
        
        const parseTimeToHours = (timeStr) => {
            if (!timeStr) return 0;
            const parts = timeStr.split(':');
            const h = parseInt(parts[0], 10);
            const m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
            return h + (m / 60);
        };

        const startH = parseTimeToHours(slot.startTime);
        let endH = parseTimeToHours(slot.endTime);
        if (endH === 0) endH = 24.0; // Assume 00:00 is end of day if it's the end time

        let dayH = 0;
        let nightH = 0;
        const nightBoundary = parseTimeToHours(slot.nightStartTime || '19:00');

        // Calculate day hours (between start and boundary)
        const dayEnd = endH < nightBoundary ? endH : nightBoundary;
        if (dayEnd > startH) dayH = dayEnd - startH;

        // Calculate night hours (between boundary and end)
        const nStart = startH > nightBoundary ? startH : nightBoundary;
        if (endH > nStart) nightH = endH - nStart;

        const dayRate = slot.pricePerHour || 0;
        const nightRate = slot.nightPricePerHour != null ? slot.nightPricePerHour : dayRate;

        return (dayH * dayRate) + (nightH * nightRate);
    };

    // Expanded sessions with individual dates
    const getExpandedSessions = () => {
        const assignments = getSelectedAssignments();
        if (!assignments.length) return [];
        const allSessions = [];

        assignments.forEach(slot => {
            const { start: slotStart, end: slotEnd } = getSlotPeriod(slot);
            const paidUntil = slot.paidUntilDate ? normalizeDate(slot.paidUntilDate) : null;

            // Geçmiş tarihler de dahil tüm dönemi göster
            const effectiveStart = slotStart;

            let current = new Date(effectiveStart);
            while (current <= slotEnd) {
                if (current.getDay() === slot.dayOfWeek) {
                    // Determine if this specific session date is paid
                    const sessionDate = new Date(current);
                    const dateKey = getDateKey(current);
                    const isCancelledDate = !!(slot.cancelledSessions || []).some(cancellation =>
                        getDateKey(cancellation.sessionDate || cancellation.SessionDate) === dateKey
                    );
                    const sessionPayment = (slot.sessionPayments || []).find(payment =>
                        getDateKey(payment.sessionDate || payment.SessionDate) === dateKey
                    );
                    const transferInfo = (slot.transfers || []).find(transfer =>
                        getDateKey(transfer.originalSessionDate || transfer.OriginalSessionDate) === dateKey
                    );
                    const isTargetDate = (slot.transfers || []).some(transfer =>
                        getDateKey(transfer.newSessionDate || transfer.NewSessionDate) === dateKey
                    );
                    const sessionIsPaid = slot.isPaid || (paidUntil && sessionDate <= paidUntil) || !!sessionPayment;

                    if (!isCancelledDate && !isTargetDate) {
                        allSessions.push({
                            ...slot,
                            sessionDate,
                            dateKey,
                            sessionKey: dateKey,
                            selectionKey: `${slot.assignmentId}_${dateKey}_${slot.startTime}_${slot.endTime}`,
                            dateDisplay: current.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }),
                            shortDate: current.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
                            isPaid: !!sessionIsPaid,
                            isTransferred: !!transferInfo || (sessionPayment ? !!sessionPayment.isTransferred : false),
                            transferTargetDate: transferInfo ? (transferInfo.newSessionDate || transferInfo.NewSessionDate) : null,
                            transferTargetStartTime: transferInfo ? (transferInfo.newStartTime || transferInfo.NewStartTime) : null,
                            transferTargetEndTime: transferInfo ? (transferInfo.newEndTime || transferInfo.NewEndTime) : null,
                            laneCount: sessionPayment ? sessionPayment.laneCount : slot.laneCount
                        });
                    }
                }
                current.setDate(current.getDate() + 1);
            }

            (slot.transfers || []).forEach(transfer => {
                const targetDate = normalizeDate(transfer.newSessionDate || transfer.NewSessionDate);
                const dateKey = getDateKey(targetDate);
                const startTime = transfer.newStartTime || transfer.NewStartTime || slot.startTime;
                const endTime = transfer.newEndTime || transfer.NewEndTime || slot.endTime;

                allSessions.push({
                    ...slot,
                    sessionDate: targetDate,
                    dateKey,
                    sessionKey: dateKey,
                    selectionKey: `${slot.assignmentId}_${dateKey}_${startTime}_${endTime}_transfer`,
                    dateDisplay: targetDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }),
                    shortDate: targetDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
                    dayOfWeek: targetDate.getDay(),
                    startTime,
                    endTime,
                    isPaid: true,
                    isTransferred: false,
                    isTransferTarget: true,
                    laneCount: slot.laneCount
                });
            });

            (slot.extraSessions || []).forEach(extraSession => {
                const extraDate = normalizeDate(extraSession.sessionDate || extraSession.SessionDate);
                const dateKey = getDateKey(extraDate);
                const startTime = extraSession.startTime || extraSession.StartTime || slot.startTime;
                const endTime = extraSession.endTime || extraSession.EndTime || slot.endTime;
                const extraSessionPayment = (slot.sessionPayments || []).find(payment =>
                    getDateKey(payment.sessionDate || payment.SessionDate) === dateKey && !payment.isTransferred
                );

                allSessions.push({
                    ...slot,
                    sessionDate: extraDate,
                    dateKey,
                    sessionKey: dateKey,
                    selectionKey: `${slot.assignmentId}_${dateKey}_${startTime}_${endTime}_extra_${extraSession.id || extraSession.Id || ''}`,
                    dateDisplay: extraDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }),
                    shortDate: extraDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
                    dayOfWeek: extraDate.getDay(),
                    startTime,
                    endTime,
                    isPaid: !!slot.isPaid || (paidUntil && extraDate <= paidUntil) || !!extraSessionPayment,
                    isTransferred: false,
                    isTransferTarget: false,
                    isExtraSession: true,
                    laneCount: extraSessionPayment ? extraSessionPayment.laneCount : (extraSession.laneCount ?? extraSession.LaneCount ?? slot.laneCount)
                });
            });
        });

        return allSessions.sort((a, b) => a.sessionDate - b.sessionDate);
    };

    const formatPeriod = () => {
        const { start, end } = getMembershipPeriod();
        const startStr = start.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
        const endStr = end.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
        return `${startStr} - ${endStr}`;
    };

    // Count how many times a weekday occurs between two dates
    const countDayOccurrences = (dayOfWeek, startDate, endDate) => {
        let count = 0;
        const start = new Date(startDate);
        const end = new Date(endDate);
        for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
            if (dt.getDay() === dayOfWeek) count++;
        }
        return count;
    };

    // Get the effective membership period (with fallback)
    const getMembershipPeriod = () => {
        const selectedBreakdown = getSelectedBreakdown();
        const start = selectedBreakdown?.membershipStartDate
            ? new Date(selectedBreakdown.membershipStartDate)
            : new Date(new Date().getFullYear(), new Date().getMonth(), 1); // fallback: start of current month
        const end = selectedBreakdown?.membershipEndDate
            ? new Date(selectedBreakdown.membershipEndDate)
            : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0); // fallback: end of current month
        return { start, end };
    };
    // Calculate payable amount based on selected mode
    const getPayableAmount = () => {
        const assignments = getSelectedAssignments();
        const debtsTotal = getSelectedDebts().reduce((sum, d) => sum + d.amount, 0);
        if (!assignments.length) return debtsTotal;

        if (isPoolFacility()) {
            let total = 0;
            const expanded = getExpandedSessions();
            expanded.forEach(session => {
                const key = session.selectionKey || `${session.assignmentId}_${session.dateKey}`;
                if (selectedPoolSessions[key] && !session.isPaid) {
                    const lanes = selectedPoolSessions[key];
                    const pricePerHour = session.lanePricePerHour > 0 ? session.lanePricePerHour : (session.pricePerHour || 0);
                    total += pricePerHour * lanes;
                }
            });
            return total + debtsTotal;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Helper: her slota ait region üyelik dönemini getir
        const unpaidSessions = getExpandedSessions().filter(session => !session.isPaid);

        if (paymentMode === 'full') {
            const total = unpaidSessions.reduce((sum, session) => sum + getEffectiveSlotPrice(session), 0);
            return total + debtsTotal;
        } else if (paymentMode === 'monthly') {
            // Aylık: önce bu ayı dene, bu ay 0 ise sonraki aya geç
            const calcMonthly = (monthOffset) => {
                const targetMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
                const monthStart = targetMonth;
                const monthEnd = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
                monthEnd.setHours(23, 59, 59, 999);

                return unpaidSessions
                    .filter(session => session.sessionDate >= monthStart && session.sessionDate <= monthEnd)
                    .reduce((sum, session) => sum + getEffectiveSlotPrice(session), 0);
            };
            const thisMonth = calcMonthly(0);
            if (thisMonth > 0) return thisMonth + debtsTotal;
            const nextMonth = calcMonthly(1);
            return nextMonth > 0 ? nextMonth + debtsTotal : debtsTotal;
        } else {
            // Daily: en yakın ödenmemiş seans gününü üyelik sonuna kadar ara
            const sessionInfo = getNextDailySessionInfo();
            if (!sessionInfo) return 0;
            // For pool facilities, use lane pricing
            if (isPoolFacility()) {
                const lanePrice = getLanePricePerHour();
                if (lanePrice > 0) {
                    return lanePrice * selectedLaneCount * sessionInfo.slotCount;
                }
            }
            return sessionInfo.total + debtsTotal;
        }
    };

    // Helper: en yakın ödenmemiş seans bilgisini döndürür
    const getNextDailySessionInfo = () => {
        const assignments = getSelectedAssignments();
        if (!assignments.length) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Her slot için kendi region bitiş tarihini kullan
        const getSlotEnd = (slot) => {
            const region = paymentInfo?.regionBreakdowns?.find(r => r.clubApplicationId === slot.clubApplicationId);
            return region?.membershipEndDate
                ? new Date(region.membershipEndDate)
                : new Date(today.getFullYear(), today.getMonth() + 1, 0);
        };

        // En uzun üyelik sonuna kadar tara
        const maxEnd = assignments.reduce((max, slot) => {
            const end = getSlotEnd(slot);
            return end > max ? end : max;
        }, today);

        // Aramaya bugün değil, en erken üyelik başlangıç tarihinden başla
        const minStart = assignments.reduce((min, slot) => {
            const region = paymentInfo?.regionBreakdowns?.find(r => r.clubApplicationId === slot.clubApplicationId);
            const start = region?.membershipStartDate ? new Date(region.membershipStartDate) : new Date(today.getFullYear(), today.getMonth(), 1);
            return start < min ? start : min;
        }, maxEnd); // Max end will be replaced by the earliest date
        
        // Ensure minStart doesn't exceed today if membership started long ago?
        // Actually we just want the earliest unpassed date.
        let checkDate = new Date(minStart);
        const maxDays = Math.ceil((maxEnd - checkDate) / (1000 * 60 * 60 * 24)) + 1;

        for (let i = 0; i < maxDays && i < 365; i++) {
            const dayDate = new Date(checkDate);
            const unpaidSlots = assignments.filter(slot => {
                if (slot.dayOfWeek !== dayDate.getDay()) return false;
                // Slotun kendi bitiş tarihini kontrol et
                const slotEnd = getSlotEnd(slot);
                if (dayDate > slotEnd) return false;
                if (slot.isPaid) return false;
                if (slot.paidUntilDate) {
                    const paidUntil = new Date(slot.paidUntilDate);
                    paidUntil.setHours(0, 0, 0, 0);
                    return dayDate > paidUntil;
                }
                return true;
            });
            if (unpaidSlots.length > 0) {
                const total = unpaidSlots.reduce((sum, s) => sum + getEffectiveSlotPrice(s), 0);
                return { date: new Date(dayDate), total, slotCount: unpaidSlots.length };
            }
            checkDate.setDate(checkDate.getDate() + 1);
        }
        return null;
    };

    // Aylık ödeme için hangi ayın ödendiğini belirle
    const getMonthlyPaymentLabel = () => {
        const assignments = getSelectedAssignments();
        if (!assignments.length)
            return new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const getSlotEnd = (slot) => {
            const region = paymentInfo?.regionBreakdowns?.find(r => r.clubApplicationId === slot.clubApplicationId);
            return region?.membershipEndDate
                ? new Date(region.membershipEndDate)
                : new Date(today.getFullYear(), today.getMonth() + 1, 0);
        };

        const getUnpaidStart = (slot, defaultStart) => {
            if (slot.isPaid) return null;
            if (slot.paidUntilDate) {
                const paidUntil = new Date(slot.paidUntilDate);
                paidUntil.setHours(0, 0, 0, 0);
                const dayAfterPaid = new Date(paidUntil);
                dayAfterPaid.setDate(dayAfterPaid.getDate() + 1);
                return dayAfterPaid > defaultStart ? dayAfterPaid : defaultStart;
            }
            return defaultStart;
        };

        // Bu ayda ödenmemiş seans var mı?
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);
        let hasThisMonth = false;
        for (const slot of assignments) {
            const slotEnd = getSlotEnd(slot);
            const effectiveEnd = slotEnd < monthEnd ? slotEnd : monthEnd;
            const unpaidStart = getUnpaidStart(slot, today);
            if (unpaidStart && unpaidStart <= effectiveEnd) {
                const occ = countDayOccurrences(slot.dayOfWeek, unpaidStart, effectiveEnd);
                if (occ > 0) { hasThisMonth = true; break; }
            }
        }
        if (hasThisMonth) return today.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
        // Sonraki ay
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        return nextMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    };

    const getPaymentModeLabel = () => {
        if (paymentMode === 'full') return 'Tüm dönem';
        if (paymentMode === 'monthly') return getMonthlyPaymentLabel();
        return 'Tek seans';
    };

    const handlePaymentSubmit = async () => {
        const errors = {};
        const pan = paymentData.cardNumber.replace(/\s/g, '');
        if (!pan || pan.length < 16) errors.cardNumber = 'Geçerli bir kart numarası giriniz.';
        if (!paymentData.cardName.trim()) errors.cardName = 'Kart üzerindeki isim gereklidir.';
        if (!paymentData.expiryMonth || !paymentData.expiryYear) errors.expiry = 'Son kullanma tarihi seçiniz.';
        if (!paymentData.cvv || paymentData.cvv.length < 3) errors.cvv = 'CVV gereklidir.';

        if (Object.keys(errors).length > 0) {
            setPaymentErrors(errors);
            return;
        }
        setPaymentErrors({});
        setPaymentLoading(true);
        setPaidAmount(getPayableAmount());

        try {
            const isPool = isPoolFacility();
            const selectedClubApplicationIds = getSelectedClubApplicationIds();
            const selectedFacilityId = getSelectedFacilityId();
            const selectedFacilityName = getCurrentFacilityName() || 'Bilinmeyen Tesis';
            const payload = {
                amount: getPayableAmount(),
                paymentGroup: 'ClubPayment',
                referenceId: String(selectedClubApplicationIds[0] || paymentInfo.clubApplicationId),
                taxNumber: paymentInfo.taxNumber,
                facilityId: selectedFacilityId,
                clubApplicationIds: selectedClubApplicationIds.length > 0
                    ? selectedClubApplicationIds
                    : (paymentInfo.clubApplicationIds || [paymentInfo.clubApplicationId]).map(Number),
                description: `Kulüp Ödeme (${isPool ? 'Seçili Seanslar' : getPaymentModeLabel()}) - ${paymentInfo.clubName} / ${selectedFacilityName}`,
                paymentMode: isPool ? 'session' : paymentMode,
                selectedSessions: isPool ? Object.keys(selectedPoolSessions).map(key => {
                    const parts = key.split('_');
                    const assignmentIdStr = parts[0];
                    const dateParts = parts[1];
                    return {
                        assignmentId: Number(assignmentIdStr),
                        sessionDate: dateParts,
                        laneCount: selectedPoolSessions[key]
                    };
                }) : undefined,
                callbackUrl: window.location.origin + '/hizmet-rehberi/spor-randevu/club-callback',
                pan,
                cv2: paymentData.cvv,
                expireMonth: paymentData.expiryMonth.padStart(2, '0'),
                expireYear: paymentData.expiryYear,
                cardType: detectCardType(paymentData.cardNumber) || '1',
                firstName: paymentData.cardName.split(' ')[0],
                lastName: paymentData.cardName.split(' ').slice(1).join(' ') || 'User',
                email: 'info@iskenderun.bel.tr'
            };

            const result = await initiateGeneralPayment(payload);
            if (result?.isSuccess && result?.data?.html) {
                setPaymentHtml(result.data.html);
            } else {
                setPaymentOutcome('failed');
                setResultMessage(result?.resultMessage || 'Ödeme başlatılamadı.');
            }
        } catch (err) {
            console.error(err);
            setPaymentOutcome('failed');
            setResultMessage('Ödeme sırasında beklenmeyen bir hata oluştu.');
        } finally {
            setPaymentLoading(false);
        }
    };

    const resetPayment = () => {
        setShowPaymentForm(false);
        setPaymentData({ cardName: '', cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
        setPaymentErrors({});
        setPaymentHtml(null);
        setPaymentOutcome(null);
        setResultMessage('');
        setPaidAmount(0);
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 16px',
        border: '1px solid #cbd5e1',
        borderRadius: '12px',
        fontSize: '15px',
        color: '#1f2937',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        background: '#fff',
        boxSizing: 'border-box'
    };

    const labelStyle = {
        display: 'block',
        fontSize: '13px',
        fontWeight: '700',
        color: '#334155',
        marginBottom: '8px'
    };

    const isSessionIncluded = (session) => {
        if (session.isPaid) return false;

        if ((session.facilityTypeName || '').toLowerCase().includes('havuz') || isPoolFacility()) {
            return !!selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`];
        }

        const sessionDate = new Date(session.sessionDate);
        sessionDate.setHours(0, 0, 0, 0);

        if (paymentMode === 'full') {
            return true;
        }

        if (paymentMode === 'monthly') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const label = getMonthlyPaymentLabel();
            const thisMonthLabel = today.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
            
            let activeMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
            let activeMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            activeMonthEnd.setHours(23, 59, 59, 999);

            if (label !== thisMonthLabel) {
                 activeMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                 activeMonthEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0);
                 activeMonthEnd.setHours(23, 59, 59, 999);
            }

            return sessionDate >= activeMonthStart && sessionDate <= activeMonthEnd;
        }

        if (paymentMode === 'daily') {
            const sessionInfo = getNextDailySessionInfo();
            if (!sessionInfo) return false;
            const targetDate = new Date(sessionInfo.date);
            targetDate.setHours(0, 0, 0, 0);
            return sessionDate.getTime() === targetDate.getTime();
        }

        return false;
    };

    return (
        <div style={{ padding: '0 1rem', paddingBottom: '2rem' }}>
            <div className="sa-facility-details-header sa-unified-tab-header">
                <div className="sa-unified-tab-header-row">
                    <h3 className="sa-unified-tab-title">
                        <div className="sa-unified-tab-icon">
                            <FaBuilding size={20} />
                        </div>
                        Kulüp Ödeme Bilgileri
                    </h3>
                </div>
                <p className="sa-unified-tab-subtitle">
                    Vergi numaranız ile kulübünüze atanan saatleri ve ödeme bilgilerinizi sorgulayabilirsiniz.
                </p>
            </div>

            {/* Informational Steps Section */}
            {!searched && (
                <div className="cp-onboarding-steps">
                    <div className="cp-step">
                        <div className="cp-step-icon">1</div>
                        <div className="cp-step-content">
                            <h4>Sorgulama</h4>
                            <p>Kulübünüzün 10 haneli vergi numarasını aşağıdaki alana girin.</p>
                        </div>
                    </div>
                    <div className="cp-step-divider"></div>
                    <div className="cp-step">
                        <div className="cp-step-icon">2</div>
                        <div className="cp-step-content">
                            <h4>İnceleme</h4>
                            <p>Kulübünüze atanan tesisleri ve saat aralıklarını detaylıca inceleyin.</p>
                        </div>
                    </div>
                    <div className="cp-step-divider"></div>
                    <div className="cp-step">
                        <div className="cp-step-icon">3</div>
                        <div className="cp-step-content">
                            <h4>Ödeme</h4>
                            <p>Toplam ödenecek tutarı görüntüleyin ve online ödeme yapın.</p>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ maxWidth: '650px', margin: '0 auto', width: '100%' }}>

                {/* Search Form */}
                <form onSubmit={handleSearch} className="cp-search-form">
                    <div className="cp-search-input-wrapper">
                        <input
                            type="text"
                            value={taxNumber}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                if (val.length <= 10) setTaxNumber(val);
                            }}
                            placeholder="10 Haneli Vergi Numaranızı Giriniz"
                            maxLength={10}
                            className="cp-search-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading || !taxNumber.trim()}
                        className="cp-search-button"
                    >
                        <FaSearch className="cp-search-icon" />
                        <span>{loading ? 'Sorgulanıyor...' : 'Sorgula'}</span>
                    </button>
                </form>

                {/* Error */}
                {error && (
                    <div className="cp-error-message">
                        <FaTimesCircle style={{ marginRight: '8px' }} />
                        {error}
                    </div>
                )}

                {/* Results */}
                {paymentInfo && !showPaymentForm && !paymentOutcome && (
                    <div>
                        {/* Club Info Card */}
                        <div style={{
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '1.25rem',
                            marginBottom: '1rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                            <h3 style={{ margin: '0 0 0.75rem', color: '#2d3748' }}>
                                {paymentInfo.clubName}
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.9rem', color: '#4a5568' }}>
                                <div><strong>Vergi No:</strong> {paymentInfo.taxNumber}</div>
                                {getSelectedBreakdown()?.membershipStartDate && getSelectedBreakdown()?.membershipEndDate && (
                                    <div><strong>Üyelik Dönemi:</strong> {new Date(getSelectedBreakdown().membershipStartDate).toLocaleDateString('tr-TR')} - {new Date(getSelectedBreakdown().membershipEndDate).toLocaleDateString('tr-TR')}</div>
                                )}
                            </div>
                            {getSelectedBreakdown()?.isExpired && (
                                <div style={{
                                    marginTop: '0.75rem',
                                    padding: '10px 14px',
                                    background: '#fef2f2',
                                    border: '1px solid #fecaca',
                                    borderRadius: '8px',
                                    color: '#991b1b',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <FaTimesCircle /> Üyelik süresi dolmuştur. Ödeme yapılabilmesi için üyelik yenilenmelidir.
                                </div>
                            )}
                        </div>

                        {/* Facility-based Summary Cards */}
                        {paymentInfo.assignments && paymentInfo.assignments.length > 0 ? (
                            <div style={{ marginBottom: '1.5rem' }}>
                                {/* Group assignments by facilityName */}
                                {(() => {
                                    const facilityGroups = {};
                                    paymentInfo.assignments.forEach(slot => {
                                        const key = slot.facilityName || 'Bilinmeyen Tesis';
                                        if (!facilityGroups[key]) facilityGroups[key] = [];
                                        facilityGroups[key].push(slot);
                                    });

                                    // Tarih bazlı genişletilmiş seansları al ve tesis bazlı grupla
                                    const allExpandedSessions = getExpandedSessions();
                                    const sessionsByFacility = {};
                                    allExpandedSessions.forEach(session => {
                                        const fName = session.facilityName || 'Bilinmeyen Tesis';
                                        if (!sessionsByFacility[fName]) sessionsByFacility[fName] = [];
                                        sessionsByFacility[fName].push(session);
                                    });

                                    // Genel toplam hesapla
                                    const { start: mStart, end: mEnd } = getMembershipPeriod();
                                    let grandTotal = 0;
                                    let grandPaid = 0;

                                    return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                            {/* Facility Tabs */}
                                            {Object.keys(facilityGroups).length > 1 && (
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '8px',
                                                    overflowX: 'auto',
                                                    paddingBottom: '8px',
                                                    marginBottom: '0.5rem',
                                                    msOverflowStyle: 'none',
                                                    scrollbarWidth: 'none'
                                                }}>
                                                    {Object.keys(facilityGroups).map((fName) => (
                                                        <button
                                                            key={fName}
                                                            onClick={() => setActiveFacilityKey(fName)}
                                                            style={{
                                                                padding: '8px 16px',
                                                                borderRadius: '50px',
                                                                border: (activeFacilityKey || Object.keys(facilityGroups)[0]) === fName ? 'none' : '1px solid #e2e8f0',
                                                                background: (activeFacilityKey || Object.keys(facilityGroups)[0]) === fName ? 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)' : '#fff',
                                                                color: (activeFacilityKey || Object.keys(facilityGroups)[0]) === fName ? '#fff' : '#64748b',
                                                                fontWeight: 700,
                                                                fontSize: '0.85rem',
                                                                cursor: 'pointer',
                                                                whiteSpace: 'nowrap',
                                                                transition: 'all 0.2s',
                                                                boxShadow: (activeFacilityKey || Object.keys(facilityGroups)[0]) === fName ? '0 4px 12px rgba(30, 64, 175, 0.2)' : 'none'
                                                            }}
                                                        >
                                                            {fName}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {Object.entries(facilityGroups).map(([facilityName, slots], fIdx) => {
                                                const currentActive = activeFacilityKey || Object.keys(facilityGroups)[0];
                                                if (facilityName !== currentActive && Object.keys(facilityGroups).length > 1) return null;

                                                const activeTab = facilityTab[facilityName] || 'unpaid';

                                                // Bu tesisin tarihli seansları
                                                const facilitySessions = sessionsByFacility[facilityName] || [];
                                                const unpaidSessions = facilitySessions.filter(s => !s.isPaid);
                                                const paidSessions = facilitySessions.filter(s => s.isPaid);
                                                const displaySessions = activeTab === 'unpaid' ? unpaidSessions : paidSessions;

                                                // Seans bazlı toplam hesapla (kısmi ödemeleri doğru yansıtır)
                                                let facilityTotal = 0;
                                                let facilityPaid = 0;
                                                facilitySessions.forEach(session => {
                                                    const price = getEffectiveSlotPrice(session);
                                                    facilityTotal += price;
                                                    if (session.isPaid) facilityPaid += price;
                                                });
                                                const facilityRemaining = facilityTotal - facilityPaid;
                                                const allPaid = facilitySessions.length > 0 && unpaidSessions.length === 0;
                                                grandTotal += facilityTotal;
                                                grandPaid += facilityPaid;

                                                const unpaidSlots = slots.filter(s => !s.isPaid);




                                                return (
                                                    <div key={fIdx} style={{
                                                        borderRadius: '14px',
                                                        overflow: 'hidden',
                                                        border: allPaid ? '2px solid #86efac' : '2px solid #e2e8f0',
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                                                        background: '#fff'
                                                    }}>
                                                        {/* Header */}
                                                        <div style={{
                                                            background: allPaid
                                                                ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                                                                : 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
                                                            padding: '14px 18px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <FaBuilding style={{ color: '#fff', fontSize: '1rem' }} />
                                                                <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>{facilityName}</span>
                                                            </div>
                                                            <div style={{
                                                                background: 'rgba(255,255,255,0.2)',
                                                                borderRadius: '50px',
                                                                padding: '4px 12px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 700,
                                                                color: '#fff',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}>
                                                                {allPaid ? (
                                                                    <><FaCheckCircle size={12} /> Tamamlandı</>
                                                                ) : (
                                                                    <><FaClock size={12} /> {unpaidSlots.length} Saat Dilimi Â· {unpaidSessions.length} Seans</>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Sekmeler: Ödenmemiş / Ödenmiş */}
                                                        <div style={{
                                                            display: 'flex',
                                                            borderBottom: '2px solid #f1f5f9',
                                                            background: '#fafbfc'
                                                        }}>
                                                            <button
                                                                onClick={() => setFacilityTab(prev => ({ ...prev, [facilityName]: 'unpaid' }))}
                                                                style={{
                                                                    flex: 1,
                                                                    padding: '10px 12px',
                                                                    border: 'none',
                                                                    background: activeTab === 'unpaid' ? '#fff' : 'transparent',
                                                                    borderBottom: activeTab === 'unpaid' ? '2px solid #dc2626' : '2px solid transparent',
                                                                    color: activeTab === 'unpaid' ? '#dc2626' : '#64748b',
                                                                    fontWeight: 700,
                                                                    fontSize: '0.82rem',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '6px',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                            >
                                                                <FaTimesCircle size={13} />
                                                                Ödenmemiş ({unpaidSessions.length})
                                                            </button>
                                                            <button
                                                                onClick={() => setFacilityTab(prev => ({ ...prev, [facilityName]: 'paid' }))}
                                                                style={{
                                                                    flex: 1,
                                                                    padding: '10px 12px',
                                                                    border: 'none',
                                                                    background: activeTab === 'paid' ? '#fff' : 'transparent',
                                                                    borderBottom: activeTab === 'paid' ? '2px solid #16a34a' : '2px solid transparent',
                                                                    color: activeTab === 'paid' ? '#16a34a' : '#64748b',
                                                                    fontWeight: 700,
                                                                    fontSize: '0.82rem',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '6px',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                            >
                                                                <FaCheckCircle size={13} />
                                                                Ödenmiş ({paidSessions.length})
                                                            </button>
                                                        </div>

                                                        {/* Sekme İçeriği: Tarih bazlı seanslar */}
                                                        <div style={{
                                                            maxHeight: '320px',
                                                            overflowY: 'auto',
                                                            padding: '0'
                                                        }}>
                                                            {displaySessions.length === 0 ? (
                                                                <div style={{
                                                                    padding: '24px 16px',
                                                                    textAlign: 'center',
                                                                    color: '#94a3b8',
                                                                    fontSize: '0.88rem'
                                                                }}>
                                                                    {activeTab === 'unpaid'
                                                                        ? 'Tüm seanslar ödenmiş durumda.'
                                                                        : 'Henüz ödenmiş seans bulunmuyor.'}
                                                                </div>
                                                            ) : (
                                                                displaySessions.map((session, sIdx) => (
                                                                    <div key={sIdx} style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        padding: '10px 16px',
                                                                        borderBottom: sIdx < displaySessions.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                                        background: isSessionIncluded(session) ? '#dcfce7' : (sIdx % 2 === 0 ? '#fff' : '#fafbfc')
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                            {(!session.isPaid && (isPoolFacility() || (session.facilityTypeName || '').toLowerCase().includes('havuz'))) ? (
                                                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative' }}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={!!selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`]}
                                                                                        onChange={(e) => {
                                                                                            const key = session.selectionKey || `${session.assignmentId}_${session.dateKey}`;
                                                                                            const checked = e.target.checked;
                                                                                            setSelectedPoolSessions(prev => {
                                                                                                const next = { ...prev };
                                                                                                if (checked) {
                                                                                                    const used = (session.usedLanesByDate && session.usedLanesByDate[session.dateKey]) || 0;
                                                                                                    const isPool = (session.facilityTypeName || '').toLowerCase().includes('havuz');
                                                                                                    const avail = isPool ? Math.max(0, 6 - used) : 1;
                                                                                                    if (avail > 0) next[key] = 1;
                                                                                                } else {
                                                                                                    delete next[key];
                                                                                                }
                                                                                                return next;
                                                                                            });
                                                                                        }}
                                                                                        disabled={((session.facilityTypeName || '').toLowerCase().includes('havuz')) && Math.max(0, 6 - ((session.usedLanesByDate && session.usedLanesByDate[session.dateKey]) || 0)) === 0}
                                                                                        style={{
                                                                                            position: 'absolute',
                                                                                            opacity: 0,
                                                                                            cursor: 'pointer',
                                                                                            height: 0,
                                                                                            width: 0
                                                                                        }}
                                                                                    />
                                                                                    <div style={{
                                                                                        height: '22px',
                                                                                        width: '22px',
                                                                                        backgroundColor: !!selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`] ? '#2563eb' : '#fff',
                                                                                        border: !!selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`] ? '2px solid #2563eb' : '2px solid #cbd5e1',
                                                                                        borderRadius: '6px',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        transition: 'all 0.2s',
                                                                                        boxShadow: !!selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`] ? '0 0 0 3px rgba(37, 99, 235, 0.1)' : 'none'
                                                                                    }}>
                                                                                        {!!selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`] && (
                                                                                            <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                                <path d="M1 5L4.5 8.5L11 2" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                            </svg>
                                                                                        )}
                                                                                    </div>
                                                                                </label>
                                                                            ) : (
                                                                                <div style={{
                                                                                    width: '6px',
                                                                                    height: '6px',
                                                                                    borderRadius: '50%',
                                                                                    background: session.isPaid ? '#16a34a' : '#dc2626',
                                                                                    flexShrink: 0
                                                                                }}></div>
                                                                            )}
                                                                            <div>
                                                                                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                                                                                    {session.dateDisplay}
                                                                                    <span style={{
                                                                                        marginLeft: '8px',
                                                                                        fontSize: '0.72rem',
                                                                                        fontWeight: 700,
                                                                                        color: '#3b82f6',
                                                                                        background: '#eff6ff',
                                                                                        padding: '1px 6px',
                                                                                        borderRadius: '4px'
                                                                                    }}>
                                                                                        {getDayName(session.dayOfWeek)}
                                                                                    </span>
                                                                                    {(session.isExtraSession || session.isTransferTarget || session.isTransferred) && (
                                                                                        <span style={{
                                                                                            marginLeft: '6px',
                                                                                            fontSize: '0.72rem',
                                                                                            fontWeight: 700,
                                                                                            color: session.isExtraSession ? '#7c3aed' : '#0284c7',
                                                                                            background: session.isExtraSession ? '#f3e8ff' : '#e0f2fe',
                                                                                            padding: '1px 6px',
                                                                                            borderRadius: '4px'
                                                                                        }}>
                                                                                            {session.isExtraSession ? 'Ek Seans' : (session.isTransferTarget ? 'Aktarılan Seans' : 'Aktarıldı')}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                                                                                    <FaClock size={10} style={{ marginRight: '4px', position: 'relative', top: '1px' }} />
                                                                                    {session.startTime} - {session.endTime}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '12px'
                                                                        }}>
                                                                            {((session.facilityTypeName || '').toLowerCase().includes('havuz')) && !session.isPaid && !!selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`] && (() => {
                                                                                const key = session.selectionKey || `${session.assignmentId}_${session.dateKey}`;
                                                                                const used = (session.usedLanesByDate && session.usedLanesByDate[session.dateKey]) || 0;
                                                                                const avail = Math.max(1, 6 - used); // Ensure at least 1 option if they selected it
                                                                                const options = Array.from({ length: avail }, (_, i) => i + 1);
                                                                                return (
                                                                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                                                                        <select
                                                                                            value={selectedPoolSessions[key]}
                                                                                            onChange={(e) => {
                                                                                                const val = Number(e.target.value);
                                                                                                setSelectedPoolSessions(prev => ({ ...prev, [key]: val }));
                                                                                            }}
                                                                                            style={{
                                                                                                appearance: 'none',
                                                                                                padding: '6px 30px 6px 12px',
                                                                                                borderRadius: '8px',
                                                                                                border: '1px solid #e2e8f0',
                                                                                                backgroundColor: '#fff',
                                                                                                fontSize: '0.8rem',
                                                                                                fontWeight: '600',
                                                                                                color: '#1e293b',
                                                                                                outline: 'none',
                                                                                                cursor: 'pointer',
                                                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                                                                transition: 'all 0.2s',
                                                                                                minWidth: '100px'
                                                                                            }}
                                                                                        >
                                                                                            {options.map(opt => <option key={opt} value={opt}>{opt} Kulvar</option>)}
                                                                                        </select>
                                                                                        <FaChevronDown
                                                                                            size={10}
                                                                                            style={{
                                                                                                position: 'absolute',
                                                                                                right: '10px',
                                                                                                color: '#64748b',
                                                                                                pointerEvents: 'none'
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                            <div style={{
                                                                                fontWeight: 700,
                                                                                color: '#0f172a',
                                                                                fontSize: '0.88rem',
                                                                                whiteSpace: 'nowrap'
                                                                            }}>
                                                                                {(() => {
                                                                                    if (isPoolFacility() && !session.isPaid && selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`]) {
                                                                                        const lanes = selectedPoolSessions[session.selectionKey || `${session.assignmentId}_${session.dateKey}`];
                                                                                        const effectivePrice = getEffectiveSlotPrice(session);
                                                                                        const price = session.lanePricePerHour > 0 ? session.lanePricePerHour : effectivePrice;
                                                                                        return (price * lanes).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
                                                                                    }
                                                                                    return getEffectiveSlotPrice(session)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 });
                                                                                })()} ₺
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>

                                                        {/* Kart Footer: Toplam / Ödenen / Kalan */}
                                                        <div style={{
                                                            padding: '12px 16px',
                                                            background: '#f8fafc',
                                                            borderTop: '2px solid #f1f5f9'
                                                        }}>
                                                            <div style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: '1fr 1fr 1fr',
                                                                gap: '8px',
                                                                textAlign: 'center'
                                                            }}>
                                                                <div>
                                                                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Toplam</div>
                                                                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#1e293b', marginTop: '2px' }}>
                                                                        {facilityTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Ödenen</div>
                                                                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#16a34a', marginTop: '2px' }}>
                                                                        {facilityPaid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Kalan</div>
                                                                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: facilityRemaining > 0 ? '#dc2626' : '#16a34a', marginTop: '2px' }}>
                                                                        {facilityRemaining > 0
                                                                            ? `${facilityRemaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`
                                                                            : <><FaCheckCircle size={14} style={{ color: '#16a34a' }} /> Tamamlandı</>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}


                                        </div>
                                    );
                                })()}
                            </div>
                        ) : (
                            <div style={{
                                background: '#fffbeb',
                                border: '1px solid #fcd34d',
                                borderRadius: '12px',
                                padding: '1rem',
                                textAlign: 'center',
                                color: '#92400e',
                                marginBottom: '1rem'
                            }}>
                                Bu kulübe henüz saat ataması yapılmamıştır.
                            </div>
                        )}

                        {/* Additional Debts Section */}
                        {getSelectedDebts().length > 0 && (
                            <div style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '12px',
                                padding: '1.25rem',
                                marginBottom: '1rem'
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.75rem', 
                                    marginBottom: '1rem',
                                    color: '#991b1b'
                                }}>
                                    <FaExclamationCircle size={20} />
                                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Ek Borçlar / Ödemeler</h3>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {getSelectedDebts().map((debt, idx) => (
                                        <div key={idx} style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            paddingBottom: idx === getSelectedDebts().length - 1 ? 0 : '0.75rem',
                                            borderBottom: idx === getSelectedDebts().length - 1 ? 'none' : '1px solid #fee2e2'
                                        }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{debt.description || 'Ek Borç'}</span>
                                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{new Date(debt.createdAt).toLocaleDateString('tr-TR')}</span>
                                            </div>
                                            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>
                                                {debt.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ 
                                    marginTop: '1rem', 
                                    paddingTop: '0.75rem', 
                                    borderTop: '2px dashed #fecaca',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#991b1b' }}>Toplam Ek Borç:</span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#dc2626' }}>
                                        {getSelectedDebts().reduce((s, d) => s + d.amount, 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                    </span>
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#b91c1c', marginTop: '0.5rem', fontStyle: 'italic' }}>
                                    * Ek borçlar toplam tutara otomatik olarak dahil edilmiştir.
                                </div>
                            </div>
                        )}

                        {/* Billing Period Info */}
                        {getSelectedAssignments().length > 0 && (
                            <div style={{
                                background: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: '12px',
                                padding: '1rem 1.25rem',
                                marginBottom: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                fontSize: '0.9rem',
                                color: '#1e3a8a'
                            }}>
                                <FaClock style={{ color: '#3b82f6', flexShrink: 0 }} />
                                <div>
                                    <strong>Dönem:</strong> {formatPeriod()} &mdash;
                                    Ücretler üyelik döneminiz boyunca haftalık olarak hesaplanmıştır.
                                </div>
                            </div>
                        )}

                        {/* Payment Mode Selector & Summary */}
                        {getSelectedAssignments().some(s => !s.isPaid) && (
                            <div>
                                {/* Mode Selector */}
                                {!isPoolFacility() && (
                                    <div style={{
                                        display: 'flex',
                                        gap: '0',
                                        marginBottom: '1rem',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        border: '2px solid #e2e8f0'
                                    }}>
                                        {[
                                            { key: 'full', label: 'Tüm Dönem', desc: formatPeriod() || 'Üyelik süresi boyunca' },
                                            { key: 'monthly', label: 'Aylık', desc: getMonthlyPaymentLabel() },
                                            { key: 'daily', label: 'Tek Seans', desc: 'Tek oturum ücreti' }
                                        ].map((mode) => (
                                            <button
                                                key={mode.key}
                                                onClick={() => setPaymentMode(mode.key)}
                                                style={{
                                                    flex: 1,
                                                    padding: '12px 8px',
                                                    border: 'none',
                                                    background: paymentMode === mode.key ? '#2563eb' : '#fff',
                                                    color: paymentMode === mode.key ? '#fff' : '#475569',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '2px',
                                                    transition: 'all 0.2s',
                                                    borderRight: mode.key !== 'daily' ? '1px solid #e2e8f0' : 'none'
                                                }}
                                            >
                                                <strong style={{ fontSize: '0.9rem' }}>{mode.label}</strong>
                                                <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>{mode.desc}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Lane Selection for Pool Facilities */}
                                {false && isPoolFacility() && getLanePricePerHour() > 0 && (
                                    <div style={{
                                        background: '#f0f9ff',
                                        border: '1px solid #bae6fd',
                                        borderRadius: '12px',
                                        padding: '1rem 1.25rem',
                                        marginBottom: '1rem'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem' }}>
                                            <FaSwimmingPool style={{ color: '#0284c7' }} />
                                            <strong style={{ color: '#0c4a6e', fontSize: '0.95rem' }}>Kulvar Secimi</strong>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                                            {[1, 2, 3, 4, 5].map(lane => (
                                                <button
                                                    key={lane}
                                                    onClick={() => setSelectedLaneCount(lane)}
                                                    style={{
                                                        padding: '10px 16px',
                                                        borderRadius: '10px',
                                                        border: selectedLaneCount === lane ? '2px solid #0284c7' : '1px solid #e0f2fe',
                                                        background: selectedLaneCount === lane ? '#0284c7' : '#fff',
                                                        color: selectedLaneCount === lane ? '#fff' : '#0c4a6e',
                                                        cursor: 'pointer',
                                                        fontWeight: '700',
                                                        fontSize: '0.9rem',
                                                        transition: 'all 0.2s',
                                                        minWidth: '50px'
                                                    }}
                                                >
                                                    {lane}
                                                </button>
                                            ))}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: '#0369a1' }}>
                                            <div style={{ marginBottom: '4px' }}>
                                                <strong>Secili:</strong> {selectedLaneCount} Kulvar x {getLanePricePerHour().toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL = <strong>{(selectedLaneCount * getLanePricePerHour()).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL/seans</strong>
                                            </div>
                                            {/* <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '6px' }}>
                                                Referans: 1-(12+1) = 1 Kulvar, 14-(24+1) = 2, 26-(36+1) = 3, 38-(48+1) = 4, 50-(60+1) = 5 Kulvar
                                            </div> */}
                                        </div>
                                    </div>
                                )}

                                {/* Payment Summary */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)',
                                    border: '2px solid #86efac',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    marginBottom: '1rem'
                                }}>
                                    <FaMoneyBillWave style={{ fontSize: '2rem', color: '#16a34a', marginBottom: '0.5rem' }} />
                                    <h4 style={{ margin: '0 0 0.5rem', color: '#166534' }}>Ödenecek Tutar ({isPoolFacility() ? 'Seçili Seanslar' : getPaymentModeLabel()})</h4>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: 'bold',
                                        color: '#16a34a'
                                    }}>
                                        {formatVatIncluded(getPayableAmount())}
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#166534', marginTop: '0.25rem' }}>
                                        {isPoolFacility() && `${Object.keys(selectedPoolSessions).length} adet seans seçildi.`}
                                        {!isPoolFacility() && paymentMode === 'full' && `${formatPeriod()} dönemi için`}
                                        {!isPoolFacility() && paymentMode === 'monthly' && `${getMonthlyPaymentLabel()} aylık ödeme`}
                                        {!isPoolFacility() && paymentMode === 'daily' && (() => {
                                            const info = getNextDailySessionInfo();
                                            if (info) {
                                                return `${info.date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' })} tarihli ${info.slotCount} seans için`;
                                            }
                                            return 'Ödenmemiş seans bulunamadı';
                                        })()}
                                    </div>
                                </div>

                                {/* Pay Now Button */}
                                <button
                                    onClick={() => setShowPaymentForm(true)}
                                    style={{
                                        width: '100%',
                                        padding: '16px 24px',
                                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '12px',
                                        fontSize: '17px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <FaCreditCard /> {formatVatIncluded(getPayableAmount())} Ödeme Yap
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Payment Form */}
                {showPaymentForm && !paymentHtml && !paymentOutcome && (
                    <div style={{
                        background: '#fff',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        padding: '30px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '24px',
                            paddingBottom: '16px',
                            borderBottom: '2px solid #e5e7eb'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button
                                    onClick={resetPayment}
                                    style={{ border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center' }}
                                    title="Geri Dön"
                                >
                                    <FaArrowLeft />
                                </button>
                                <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Kart Bilgileri</h4>
                            </div>
                            <div style={{
                                fontWeight: '700',
                                color: '#2563eb',
                                fontSize: '16px',
                                background: '#eff6ff',
                                padding: '8px 16px',
                                borderRadius: '20px'
                            }}>
                                {formatVatIncluded(getPayableAmount())}
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Kart Numarası</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    placeholder="0000 0000 0000 0000"
                                    maxLength={19}
                                    value={paymentData.cardNumber}
                                    onChange={e => {
                                        const val = e.target.value.replace(/\D/g, '');
                                        const formatted = val.match(/.{1,4}/g)?.join(' ') || val;
                                        if (val.length <= 16) setPaymentData({ ...paymentData, cardNumber: formatted });
                                        if (paymentErrors.cardNumber) setPaymentErrors({ ...paymentErrors, cardNumber: '' });
                                    }}
                                    style={{
                                        ...inputStyle,
                                        borderColor: paymentErrors.cardNumber ? '#ef4444' : '#cbd5e1',
                                        paddingRight: '50px'
                                    }}
                                />
                                <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)' }}>
                                    {detectCardType(paymentData.cardNumber.replace(/\s/g, '')) === '1' ? <FaCcVisa size={28} color="#1a1f71" /> :
                                        detectCardType(paymentData.cardNumber.replace(/\s/g, '')) === '2' ? <FaCcMastercard size={28} color="#f97316" /> :
                                            <FaCreditCard size={24} color="#cbd5e1" />}
                                </div>
                            </div>
                            {paymentErrors.cardNumber && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cardNumber}</div>}
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Kart Üzerindeki İsim</label>
                            <input
                                type="text"
                                placeholder="AD SOYAD"
                                value={paymentData.cardName}
                                onChange={e => {
                                    setPaymentData({ ...paymentData, cardName: e.target.value.toUpperCase() });
                                    if (paymentErrors.cardName) setPaymentErrors({ ...paymentErrors, cardName: '' });
                                }}
                                style={{
                                    ...inputStyle,
                                    borderColor: paymentErrors.cardName ? '#ef4444' : '#cbd5e1'
                                }}
                            />
                            {paymentErrors.cardName && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cardName}</div>}
                        </div>

                        <div style={{ display: 'flex', gap: '20px', marginBottom: '32px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Son Kullanma Tarihi</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <select
                                        value={paymentData.expiryMonth}
                                        onChange={e => {
                                            setPaymentData({ ...paymentData, expiryMonth: e.target.value });
                                            if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                        }}
                                        style={{ ...inputStyle, flex: 1, borderColor: paymentErrors.expiry ? '#ef4444' : '#cbd5e1' }}
                                    >
                                        <option value="">Ay</option>
                                        {Array.from({ length: 12 }, (_, i) => <option key={i} value={String(i + 1).padStart(2, '0')}>{String(i + 1).padStart(2, '0')}</option>)}
                                    </select>
                                    <select
                                        value={paymentData.expiryYear}
                                        onChange={e => {
                                            setPaymentData({ ...paymentData, expiryYear: e.target.value });
                                            if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                        }}
                                        style={{ ...inputStyle, flex: 1, borderColor: paymentErrors.expiry ? '#ef4444' : '#cbd5e1' }}
                                    >
                                        <option value="">Yıl</option>
                                        {Array.from({ length: 15 }, (_, i) => <option key={i} value={String(2025 + i)}>{2025 + i}</option>)}
                                    </select>
                                </div>
                                {paymentErrors.expiry && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.expiry}</div>}
                            </div>

                            <div style={{ width: '120px' }}>
                                <label style={labelStyle}>CVV</label>
                                <input
                                    type="text"
                                    placeholder="123"
                                    maxLength={3}
                                    value={paymentData.cvv}
                                    onChange={e => {
                                        const val = e.target.value.replace(/\D/g, '');
                                        if (val.length <= 3) setPaymentData({ ...paymentData, cvv: val });
                                        if (paymentErrors.cvv) setPaymentErrors({ ...paymentErrors, cvv: '' });
                                    }}
                                    style={{ ...inputStyle, borderColor: paymentErrors.cvv ? '#ef4444' : '#cbd5e1' }}
                                />
                                {paymentErrors.cvv && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cvv}</div>}
                            </div>
                        </div>

                        <button
                            onClick={handlePaymentSubmit}
                            disabled={paymentLoading}
                            style={{
                                width: '100%',
                                padding: '16px 24px',
                                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '17px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                opacity: paymentLoading ? 0.7 : 1
                            }}
                        >
                            {paymentLoading ? 'İşleniyor...' : <><FaCheckCircle /> Ödemeyi Tamamla</>}
                        </button>
                    </div>
                )}

                {/* 3D Secure iFrame */}
                {paymentHtml && !paymentOutcome && (
                    <div style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '14px',
                        overflow: 'hidden',
                        boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
                        background: '#fff'
                    }}>
                        <div style={{
                            padding: '12px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0'
                        }}>
                            <button
                                onClick={() => { setPaymentHtml(null); }}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#334155',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    padding: '8px 10px',
                                    borderRadius: '8px'
                                }}
                            >
                                <FaArrowLeft /> Geri
                            </button>
                            <strong style={{ color: '#0f172a', fontSize: '15px' }}>
                                {formatVatIncluded(getPayableAmount())}
                            </strong>
                        </div>
                        <iframe
                            ref={iframeRef}
                            title="3D Secure Payment"
                            srcDoc={paymentHtml}
                            onLoad={handleIframeLoad}
                            style={{ width: '100%', height: '600px', border: 'none', display: 'block', background: '#fff' }}
                        />
                    </div>
                )}

                {/* Payment Result */}
                {paymentOutcome && (
                    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                        <div style={{
                            fontSize: '60px',
                            marginBottom: '20px',
                            color: paymentOutcome === 'success' ? '#22c55e' : '#ef4444'
                        }}>
                            {paymentOutcome === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
                        </div>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '700',
                            color: paymentOutcome === 'success' ? '#15803d' : '#b91c1c',
                            marginBottom: '12px'
                        }}>
                            {paymentOutcome === 'success' ? 'Ödeme Başarılı!' : 'Ödeme Başarısız'}
                        </h2>
                        <p style={{
                            fontSize: '16px',
                            color: paymentOutcome === 'success' ? '#166534' : '#991b1b',
                            marginBottom: '30px',
                            maxWidth: '400px',
                            margin: '0 auto 30px'
                        }}>
                            {resultMessage}
                        </p>

                        {paymentOutcome === 'success' && (
                            <div style={{
                                background: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '12px',
                                padding: '20px',
                                marginBottom: '30px',
                                textAlign: 'left'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ color: '#166534', fontWeight: '600' }}>Kulüp</span>
                                    <span style={{ color: '#15803d' }}>{paymentInfo?.clubName}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#166534', fontWeight: '600' }}>Ödenen Tutar</span>
                                    <span style={{ color: '#15803d', fontWeight: 'bold' }}>{formatVatIncluded(paidAmount)}</span>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                            {paymentOutcome === 'failed' && (
                                <button
                                    onClick={() => { setPaymentOutcome(null); setPaymentHtml(null); }}
                                    style={{
                                        padding: '12px 24px',
                                        borderRadius: '10px',
                                        background: '#2563eb',
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Tekrar Dene
                                </button>
                            )}
                            <button
                                onClick={() => { resetPayment(); setPaymentInfo(null); setSearched(false); }}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    background: paymentOutcome === 'success' ? '#2563eb' : '#ef4444',
                                    color: '#fff',
                                    border: 'none',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                {paymentOutcome === 'success' ? 'Yeni Sorgulama' : 'İptal Et'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Empty state when searched but no result */}
                {searched && !loading && !paymentInfo && !error && (
                    <div style={{
                        background: '#f8fafc',
                        borderRadius: '12px',
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#94a3b8'
                    }}>
                        Sorgulanacak vergi numarası giriniz.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClubPayment;

